<template>
    <page-loading v-if="loading"></page-loading>
    <form-container v-else>
        <step-form-heading v-if="!props.snippetId"
            :icon="faClipboard">
            Snippet Template

            <template v-slot:description>
                Create a Snippet and share it with Groups.
            </template>
        </step-form-heading>

        <page-header v-else
            class="mb-6 lg:mb-12">
            <template v-slot:subtitle>
                <a href="/messaging/snippets/templates"
                    class="no-underline! text-sky-600! text-base font-normal">
                    <font-awesome-icon widthAuto class="mr-1"
                        :icon="faArrowLeft"></font-awesome-icon>
                    All Snippet Templates
                </a>
            </template>

            {{ snippet.title }}
        </page-header>

        <div class="grid grid-cols-12">
            <page-sidebar v-if="props.snippetId"
                :links="[
                    { href: `/messaging/snippets/templates/${props.snippetId}`, label: 'Details' },
                ]">
            </page-sidebar>

            <form-wrapper
                :sections="[
                    { id: 'title', label: 'Name' },
                    { id: 'description', label: 'Description' },
                    { id: 'snippet', label: 'Snippet' },
                    { id: 'groups', label: 'Groups' },
                ]"
                :hideNav="Boolean(props.snippetId)"
            >


                <vx-expandable-card id="title">
                    <template v-slot:header> Name </template>

                    <template v-slot:content>
                        <vx-input v-model="snippet.title"
                            :disabled="disableLocked"
                            data-test="title"
                            :error="displayFirstError(validation.title)"
                            autofocus
                            size="lg"
                            :maxlength="120"></vx-input>
                    </template>
                </vx-expandable-card>

                <vx-expandable-card id="description">
                    <template v-slot:header> Description </template>

                    <template v-slot:content>
                        <vx-input v-model="snippet.description"
                            :disabled="disableLocked"
                            data-test="description"
                            :error="displayFirstError(validation.description)"
                            autofocus
                            autoresize
                            :rows="3"
                            size="lg"
                            :maxlength="2000"></vx-input>
                    </template>
                </vx-expandable-card>

                <vx-expandable-card id="type">
                    <template v-slot:header> Type </template>

                    <template v-slot:content>
                        <vx-radio-selection v-model="snippetType" :disabled="disableLocked">
                            <vx-radio-selection-item value="regular">
                                <template v-slot:header>
                                Regular
                                </template>
                                <template v-slot:content>A starting place for a message. Great for when you want to allow freedom to customize.</template>
                            </vx-radio-selection-item>

                            <vx-radio-selection-item value="locked">
                                <template v-slot:header>
                                Locked
                                </template>
                                <template v-slot:content>Message content is blocked from editing, only defined fields can be adjusted.</template>
                            </vx-radio-selection-item>
                        </vx-radio-selection>

                        <vx-alert v-if="snippetType === 'locked'" color="info" class="mt-4" :closeable="false">
                            Locked Snippets cannot be updated once created.
                        </vx-alert>
                    </template>
                </vx-expandable-card>

                <vx-expandable-card id="snippet">
                    <template v-slot:header> Snippet </template>
                    <template v-slot:content>
                        <vx-label>Message</vx-label>
                        <message-builder ref="messageBuilderRef"
                            v-model="snippet.body"
                            v-model:mediaUrl="snippet.media_url"
                            v-model:mediaContentType="snippet.media_content_type"
                            :disabled="disableLocked"
                            disableSnippets
                            testable
                            :error="displayFirstError(validation.body)"
                        >
                            <template v-if="snippet.is_locked" v-slot:actions>
                                <vx-dropdown-item @click="addFieldModal = true">
                                    <span>
                                        <font-awesome-icon widthAuto :icon="faSquareDashedCirclePlus"
                                        class="mr-1 text-sm"></font-awesome-icon>
                                        Field
                                    </span>
                                </vx-dropdown-item>
                            </template>
                        </message-builder>

                        <snippet-template-form-fields
                            v-model="snippetFields"
                            v-model:adding="addFieldModal"
                            :body="snippet.body"
                            @added="messageBuilderRef?.insertContent(`{{field=>${$event.key}}}`)"
                            :disabled="disableLocked"
                        >
                        </snippet-template-form-fields>
                    </template>
                </vx-expandable-card>


                <snippet-template-form-group-access id="groups" :snippetId="props.snippetId ? Number(props.snippetId) : undefined"
                    v-model:groupFilters="snippet.group_filters"
                    v-model:groupCollectionId="snippet.group_collection_id" :disabled="disableLocked">
                </snippet-template-form-group-access>


                <vx-sticky-footer>
                    <vx-button href="/messaging/snippets/templates"
                        class="order-3 lg:w-20 lg:order-1"
                        size="lg"
                        color="muted-dark">
                        Cancel
                    </vx-button>
                    <vx-popover
                        class="order-1 lg:grow [&>div]:lg:w-full lg:order-3"
                        arrow
                        hover
                        placement="top"
                    >
                        <vx-button @click="submit()"
                            data-test="submit"
                            :disabled="disableLocked"
                            :loading="processing"
                            type="button"
                            block
                            size="lg"
                            color="primary">
                            {{ props.snippetId ? 'Update' : 'Create' }} Snippet Template
                        </vx-button>
                        <template v-if="disableLocked" v-slot:content>
                            Locked Snippets cannot be updated.
                        </template>
                    </vx-popover>

                </vx-sticky-footer>

            </form-wrapper>
        </div>
    </form-container>
</template>

<script setup lang="ts">
import { faArrowLeft, faClipboard, faSquareArrowUpLeft, faSquareDashedCirclePlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxLabel, VxDropdownItem, VxButton, VxExpandableCard, VxInput, VxStickyFooter, VxRadioSelection, VxRadioSelectionItem, VxSwal, VxAlert, VxPopover } from '@voxie/frontend-components';
import { maxLength, required } from '@vuelidate/validators';
import { ref, useTemplateRef, computed } from 'vue';
import PageHeader from '~/components/general/PageHeader.vue';
import PageSidebar from '~/components/general/PageSidebar.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import FormContainer from '~/components/general/FormContainer.vue';
import { useFormSubmission } from '~/composables/useFormSubmission';
import { displayFirstError } from '~/utils/validation';
import StepFormHeading from '~/components/general/StepFormHeading.vue';
import { snippetsClient } from '~/services';
import MessageBuilder from '~/components/general/message-builder/MessageBuilder.vue';
import SnippetTemplateFormGroupAccess from './SnippetTemplateFormGroupAccess.vue';
import SnippetTemplateFormFields from './SnippetTemplateFormFields.vue';
import FormWrapper from '../general/FormWrapper.vue';
import type { Snippet } from '~/services/snippets/snippetsClient.types';

const teamId = Spark.state.currentTeam.id;

const loading = ref(false);

const messageBuilderRef = useTemplateRef('messageBuilderRef');

const defaultData = (): Snippet => {
    return {
        title: '',
        description: '',
        is_locked: false,
        body: '',
        group_filters: [],
        group_collection_id: undefined,
        fields:[]
    };
};

const snippet = ref(defaultData());
const snippetType = computed<'regular' | 'locked'>({
    set: (value) => {
        if (snippet.value.fields?.length && value === 'regular') {
            VxSwal.confirmation(
                'Change Type',
                'Are you sure? Changing the type will reset the fields in the snippet template message.'
            ).then((confirmed) => {
                if(!confirmed){
                    return;
                }
                snippet.value.is_locked = false;
                snippet.value.fields = [];
            }).catch(() => {});
        } else {
            snippet.value.is_locked = value === 'locked';
        }
    },
    get: () => {
        return snippet.value.is_locked ? 'locked' : 'regular';
    },
});

const snippetFields = computed({
    get: () => snippet.value.fields ?? [],
    set: (value) => { snippet.value.fields = value; }
});

const props = defineProps({
    snippetId: {
        type: [Number, String],
    }
})

if (props.snippetId) {
    loading.value = true;
    snippetsClient.getSnippet({ teamId }, Number(props.snippetId))
        .then(response => {
            snippet.value = {
                ...response.data,
                fields: response.data.fields || []
            };
            loading.value = false;
        });
}

const { validation, submit, processing } = useFormSubmission({
    data: snippet,
    validation: {
        title: {
            required,
            maxLength: maxLength(120)
        },
        description: {
            required,
            maxLength: maxLength(2000)
        },
        body: {
            required,
            maxLength: maxLength(1600)
        }
    },
    submit: async () => {
        const snippetData = { ...snippet.value, is_template: true };

        if (snippetData.group_collection_id === undefined) {
            delete snippetData.group_collection_id;
        }

        if (props.snippetId) {
            await snippetsClient.updateSnippet({ teamId },Number(props.snippetId), snippetData);
        } else {
            await snippetsClient.createSnippet({ teamId }, snippetData);
        }
    },
    preserveScroll: () => Boolean(props.snippetId),
    reset: () => {
        if (!props.snippetId) {
            snippet.value = defaultData();
        }
    },
    notification: () => ({
        message: `Snippet Template ${props.snippetId ? 'updated' : 'created'}`,
        action: {
            icon: faSquareArrowUpLeft,
            label: 'Back to Snippet Templates',
            href: '/messaging/snippets/templates',
        },
    })
});

const addFieldModal = ref(false);

const disableLocked = computed(()=>{
    return Boolean(props.snippetId && snippet.value.is_locked);
})

</script>
